import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./NotesPage.css";
import DepartmentMasterModal from "./DepartmentMasterModal";

const API = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:5003" : "")
).replace(/\/+$/, "");

const authHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const BLOCK_ICONS = {
  paragraph: "¶",
  sketch: "✏",
  points: "•",
  image: "▧",
  document: "📄",
  link: "🔗",
  subheading: "H",
  checklist: "✓",
  template: "▦",
};

const TrashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const NotesPage = () => {
  const navigate = useNavigate();

  const [notes, setNotes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [canManageDepartments, setCanManageDepartments] = useState(false);
  const [showDepartmentMaster, setShowDepartmentMaster] = useState(false);

  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    department: "",
    project: "",
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setLoadError("");

    // Load independently: a broken masters route must not hide existing notes.
    const [notesResult, mastersResult, departmentsResult] =
      await Promise.allSettled([
        axios.get(`${API}/api/notes`, { headers: authHeaders() }),
        axios.get(`${API}/api/notes/masters`, { headers: authHeaders() }),
        axios.get(`${API}/api/notes/departments`, { headers: authHeaders() }),
      ]);

    if (notesResult.status === "fulfilled") {
      const payload = notesResult.value.data;
      const notesList = Array.isArray(payload?.notes)
        ? payload.notes
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : null;

      if (notesList) {
        setNotes(notesList);
      } else {
        console.error("Unexpected Notes response:", payload);
        setLoadError("Notes API did not return a notes array.");
      }
    } else {
      const error = notesResult.reason;
      console.error(
        "Notes API error:",
        error.response?.status,
        error.response?.data,
      );
      setLoadError(
        error.response?.data?.message ||
          error.message ||
          "Unable to load notes.",
      );
    }

    if (mastersResult.status === "fulfilled") {
      const masters = mastersResult.value.data || {};
      setProjects(Array.isArray(masters.projects) ? masters.projects : []);
      setDepartments(
        Array.isArray(masters.departments) ? masters.departments : [],
      );
      setCanManageDepartments(masters.canManageDepartments === true);
    } else {
      console.error(
        "Notes masters API error:",
        mastersResult.reason.response?.status,
        mastersResult.reason.response?.data,
      );
    }

    // Department endpoint provides the authoritative Notes-only list and permission.
    if (departmentsResult.status === "fulfilled") {
      const data = departmentsResult.value.data || {};
      const list = Array.isArray(data.departments) ? data.departments : [];
      setDepartments(list.filter((item) => item.isActive !== false));
      setCanManageDepartments(data.canManageDepartments === true);
    } else {
      console.error(
        "Department Master API error:",
        departmentsResult.reason.response?.status,
        departmentsResult.reason.response?.data,
      );
    }

    setLoading(false);
  };

  const filteredNotes = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return (Array.isArray(notes) ? notes : []).filter((note) => {
      const departmentName = note.department?.name || note.departmentName || "";
      const projectName = note.project?.name || "";

      const matchesSearch =
        !keyword ||
        String(note.name || "")
          .toLowerCase()
          .includes(keyword) ||
        String(departmentName).toLowerCase().includes(keyword) ||
        String(projectName).toLowerCase().includes(keyword);

      const matchesDepartment =
        !departmentFilter ||
        String(note.department?._id || note.department) === departmentFilter;

      return matchesSearch && matchesDepartment;
    });
  }, [notes, search, departmentFilter]);

  const openCreateModal = () => {
    setForm({
      name: "",
      department: "",
      project: "",
    });

    setShowCreate(true);
  };

  const createNote = async () => {
    if (!form.name.trim()) {
      alert("Please enter note name.");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token");

      if (!token) {
        alert("Login session not found. Please login again.");
        return;
      }

      const response = await axios.post(
        `${API}/api/notes`,
        {
          name: form.name.trim(),
          department: form.department || null,
          project: form.project || null,
          blocks: [],
        },
        {
          headers: authHeaders(),
        },
      );

      const note = response.data?.note || response.data?.data || response.data;

      setShowCreate(false);

      if (note?._id) {
        navigate(`/tools/notes/${note._id}`);
      } else {
        await loadData();
      }
    } catch (error) {
      console.error("Create note error:", error);

      alert(error.response?.data?.message || "Unable to create note.");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (event, note) => {
    event.stopPropagation();

    if (!window.confirm(`Delete "${note.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/notes/${note._id}`, {
        headers: authHeaders(),
      });

      setNotes((previous) =>
        (Array.isArray(previous) ? previous : []).filter(
          (item) => item._id !== note._id,
        ),
      );
    } catch (error) {
      console.error("Delete note error:", error);

      alert(
        error.response?.data?.message || "Unable to delete note.",
      );
    }
  };

  return (
    <div className="notes-dashboard">
      <div className="notes-dashboard-header">
        <div>
          <h1>Notes</h1>

          <p>Create and organize project and department notes.</p>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="ndm-master-button"
            onClick={() => {
              if (canManageDepartments) {
                setShowDepartmentMaster(true);
              } else {
                alert(
                  "Department Master is visible, but the backend has not granted this login admin access. " +
                    "Check GET /api/notes/departments → canManageDepartments and the logged-in user's role.",
                );
              }
            }}
          >
            ⚙ Department Master
          </button>
          <button className="notes-add-button" onClick={openCreateModal}>
            <span>＋</span>
            Add Note
          </button>
        </div>
      </div>

      <div className="notes-filter-card">
        <div className="notes-search">
          <span>⌕</span>

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search notes..."
          />
        </div>

        <select
          value={departmentFilter}
          onChange={(event) => setDepartmentFilter(event.target.value)}
        >
          <option value="">All Departments</option>

          {departments.map((department) => (
            <option key={department._id} value={department._id}>
              {department.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="notes-empty">Loading notes...</div>
      ) : loadError ? (
        <div className="notes-empty" role="alert">
          <h2>Unable to load notes</h2>
          <p>{loadError}</p>
          <button className="notes-add-button" type="button" onClick={loadData}>
            Retry
          </button>
        </div>
      ) : filteredNotes.length === 0 ? (
        <div className="notes-empty">
          <div className="notes-empty-icon">📝</div>

          <h2>No notes yet</h2>

          <p>Create your first note to start organizing information.</p>

          <button className="notes-add-button" onClick={openCreateModal}>
            ＋ Add Note
          </button>
        </div>
      ) : (
        <div className="notes-card-grid">
          {filteredNotes.map((note) => {
            const departmentName =
              note.department?.name || note.departmentName || "";
            const blocks = Array.isArray(note.blocks) ? note.blocks : [];

            return (
              <div
                key={note._id}
                className="nc-card"
                onClick={() => navigate(`/tools/notes/${note._id}`)}
              >
                <div className="nc-top">
                  <div className="nc-icon">📝</div>

                  <button
                    type="button"
                    className="nc-delete"
                    title="Delete note"
                    aria-label="Delete note"
                    onClick={(event) => deleteNote(event, note)}
                  >
                    <TrashIcon />
                  </button>
                </div>

                <h3 className="nc-title" title={note.name}>
                  {note.name}
                </h3>

                <div className="nc-tags">
                  {departmentName && (
                    <span className="nc-tag nc-tag--dept">
                      🏢 {departmentName}
                    </span>
                  )}

                  {note.project?.name && (
                    <span className="nc-tag nc-tag--project">
                      📁 {note.project.name}
                    </span>
                  )}

                  {!departmentName && !note.project?.name && (
                    <span className="nc-tag nc-tag--muted">
                      No department / project
                    </span>
                  )}
                </div>

                <div className="nc-blocks">
                  {blocks.slice(0, 5).map((block, index) => (
                    <span
                      key={block._id || index}
                      className="nc-block"
                      title={block.type}
                    >
                      {BLOCK_ICONS[block.type] || "•"}
                    </span>
                  ))}

                  {blocks.length > 5 && (
                    <span className="nc-block nc-block--more">
                      +{blocks.length - 5}
                    </span>
                  )}
                </div>

                <div className="nc-footer">
                  <span>
                    {blocks.length} component{blocks.length === 1 ? "" : "s"}
                  </span>

                  <span>
                    {note.updatedAt
                      ? new Date(note.updatedAt).toLocaleDateString()
                      : ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showDepartmentMaster && canManageDepartments && (
        <DepartmentMasterModal
          onClose={() => setShowDepartmentMaster(false)}
          onChanged={loadData}
        />
      )}

      {/* CREATE NOTE MODAL */}

      {showCreate && (
        <div
          className="note-modal-overlay"
          onMouseDown={() => setShowCreate(false)}
        >
          <div
            className="note-create-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="note-modal-header">
              <div>
                <h2>Create New Note</h2>

                <p>Select department and project.</p>
              </div>

              <button onClick={() => setShowCreate(false)}>×</button>
            </div>

            <div className="note-modal-body">
              <label>Note Name *</label>

              <input
                value={form.name}
                onChange={(event) =>
                  setForm({
                    ...form,
                    name: event.target.value,
                  })
                }
                placeholder="Enter note name"
                autoFocus
              />

              <label>Department</label>

              <select
                value={form.department}
                onChange={(event) =>
                  setForm({
                    ...form,
                    department: event.target.value,
                  })
                }
              >
                <option value="">Select Department</option>

                {departments.map((department) => (
                  <option key={department._id} value={department._id}>
                    {department.name}
                  </option>
                ))}
              </select>

              <label>Project</label>

              <select
                value={form.project}
                onChange={(event) =>
                  setForm({
                    ...form,
                    project: event.target.value,
                  })
                }
              >
                <option value="">Select Project</option>

                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="note-modal-footer">
              <button
                className="note-cancel-button"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>

              <button
                className="notes-add-button"
                onClick={createNote}
                disabled={saving}
              >
                {saving ? "Creating..." : "Create Note"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPage;